// ========================================
// HOME.JSX - TELA INICIAL CRIATIVA
// src/components/Home.jsx
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from './Loading';
import { 
  FilePlus, ClipboardList, BarChart3, TrendingUp, TrendingDown,
  Calendar, Clock, DollarSign, Package, Users, CheckCircle2, XCircle,
  AlertTriangle, ChevronRight, Sparkles, Target, Award, Zap,
  RefreshCw, Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { pedidoAPI } from '../services/api';

// ========================================
// PALETA DE CORES
// ========================================
const cores = {
  azulEscuro: '#1F3D7A',
  azulMedio: '#0D5DFC',
  ciano: '#00BEFF',
  verde: '#10B981',
  vermelho: '#EF4444',
  amarelo: '#F59E0B',
  laranja: '#F97316',
  roxo: '#8B5CF6',
  rosa: '#EC4899'
};

// ========================================
// COMPONENTE PRINCIPAL
// ========================================
const Home = ({ onNavigate }) => {
  const { usuario, temPermissao, isAdminOuDiretoria } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dados, setDados] = useState({
    pedidosHoje: 0,
    pedidosSemana: 0,
    pedidosMes: 0,
    valorHoje: 0,
    valorSemana: 0,
    valorMes: 0,
    pedidosPorDia: [],
    meusPedidosHoje: 0,
    meusPedidosMes: 0,
    meuValorMes: 0,
    topVendedores: [],
    ultimosPedidos: [],
    faturado: null // { hoje, semana, mes: { quantidade, valor } } — null se a busca falhar
  });

  // Usar a função do contexto - mais confiável
  const ehAdminOuDiretoria = isAdminOuDiretoria();

  // Verificar se é Administrativo (pode ver tudo, mas não aprovar)
  const ehAdministrativo = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase().trim();
    return dep === 'administrativo';
  };

  // Pode ver todos os dados (Admin, Diretoria ou Administrativo)
  const podeVerTodosDados = ehAdminOuDiretoria || ehAdministrativo();

  // ========================================
  // CALCULAR STATUS GERAL DO PEDIDO (baseado na pontuação)
  // ========================================
  const getStatusByScore = (pontuacao) => {
    const p = Math.round((pontuacao || 0) * 100) / 100;
    if (p >= 40) return 'EXCELENTE';
    if (p >= 20) return 'BOA VENDA';
    if (p > 0) return 'VIÁVEL';
    if (p >= -20) return 'EM ANÁLISE';
    return 'INVIÁVEL';
  };

  const calcularStatusGeral = (pedido) => {
    // Se já tem status_geral salvo no banco, usar ele diretamente
    if (pedido.status_geral) {
      return pedido.status_geral;
    }

    // Fallback: calcular status geral se não estiver no banco (pedidos antigos)
    const valorTab1 = parseFloat(pedido.valor_tabela1) || 0;
    const valorTab2 = parseFloat(pedido.valor_tabela2) || 0;
    const temTab1 = valorTab1 > 0;
    const temTab2 = valorTab2 > 0;
    
    // Se não tem nenhuma tabela, retornar N/A
    if (!temTab1 && !temTab2) return 'N/A';
    
    // Se tem pontuacao_geral no banco, usar ela
    if (pedido.pontuacao_geral !== null && pedido.pontuacao_geral !== undefined) {
      const totalVenda = valorTab1 + valorTab2;
      return totalVenda === 0 ? 'INVIÁVEL' : getStatusByScore(pedido.pontuacao_geral);
    }
    
    // Fallback: calcular pontuação se não tiver no banco
    const pontTab1 = temTab1 ? Math.min(parseFloat(pedido.pontuacao_tabela1) || 0, 50) : 0;
    const pontTab2 = temTab2 ? Math.min(parseFloat(pedido.pontuacao_tabela2) || 0, 50) : 0;
    const pontuacaoGeral = pontTab1 + pontTab2;
    
    const totalVenda = valorTab1 + valorTab2;
    return totalVenda === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeral);
  };

  // ========================================
  // FORMATAR DATA DO PEDIDO
  // ========================================
  const formatarDataPedido = (pedido) => {
    try {
      // Se tem campo 'data' no formato YYYY-MM-DD
      if (pedido.data) {
        // Parsear a data diretamente do formato YYYY-MM-DD
        const partes = pedido.data.split('-');
        if (partes.length === 3) {
          const ano = parseInt(partes[0], 10);
          const mes = parseInt(partes[1], 10);
          const dia = parseInt(partes[2], 10);
          // Validar se são números válidos
          if (!isNaN(ano) && !isNaN(mes) && !isNaN(dia)) {
            // Retornar no formato DD/MM/YYYY
            return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
          }
        }
      }
      
      // Se tem created_at, usar ele
      if (pedido.created_at) {
        const data = new Date(pedido.created_at);
        if (!isNaN(data.getTime())) {
          return data.toLocaleDateString('pt-BR');
        }
      }
      
      return '-';
    } catch (e) {
      console.error('Erro ao formatar data:', e);
      return '-';
    }
  };

  // ========================================
  // CARREGAR DADOS
  // ========================================
  const carregarDados = async (showRefresh = false) => {
    // Verificar se usuário está carregado
    if (!usuario) {
      return;
    }

    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // Usar a variável já calculada do contexto
      
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString();
      const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59).toISOString();

      // Buscar todos os pedidos do mês para cálculos via API
      // + totais faturados (se o resumo falhar, a Home continua com o resto)
      const [pedidosMes, faturado] = await Promise.all([
        pedidoAPI.buscarPorPeriodo(inicioMes, fim),
        pedidoAPI.buscarResumoFaturado().catch(err => {
          console.error('Erro ao carregar resumo faturado:', err);
          return null;
        })
      ]);

      // Debug: verificar dados dos pedidos
      if (pedidosMes?.length > 0) {
      }

      if (!pedidosMes) {
        console.error('Erro ao carregar pedidos');
        return;
      }

      // Filtrar por período - usar campo 'data' se existir, senão 'created_at'
      // Função para obter a data do pedido no formato YYYY-MM-DD
      const getDataPedidoStr = (p) => {
        if (p.data) {
          // Campo 'data' pode vir como string YYYY-MM-DD ou como Date/ISO string
          // Se for string ISO com timezone (ex: 2026-01-07T00:00:00.000Z), extrair apenas a parte da data
          const dataStr = String(p.data);
          if (dataStr.includes('T')) {
            // É uma string ISO, extrair apenas YYYY-MM-DD
            return dataStr.split('T')[0];
          }
          // Já está no formato YYYY-MM-DD
          return dataStr.substring(0, 10);
        }
        // Usar created_at convertido para fuso de São Paulo
        const createdDate = new Date(p.created_at);
        return createdDate.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      };

      // Data de hoje no formato YYYY-MM-DD (fuso de São Paulo)
      const hojeStr = hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

      const pedidosHoje = pedidosMes?.filter(p => {
        const dataPedidoStr = getDataPedidoStr(p);
        return dataPedidoStr === hojeStr;
      }) || [];

      // Início da semana (domingo)
      const inicioSemanaDate = new Date(hoje);
      inicioSemanaDate.setDate(hoje.getDate() - hoje.getDay());
      const inicioSemanaStr = inicioSemanaDate.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

      const pedidosSemana = pedidosMes?.filter(p => {
        const dataPedidoStr = getDataPedidoStr(p);
        return dataPedidoStr >= inicioSemanaStr;
      }) || [];

      // Calcular valores (usando valor_tabela1 + valor_tabela2)
      const calcularValor = (pedidos) => {
        return pedidos.reduce((acc, p) => {
          const v1 = parseFloat(p.valor_tabela1) || 0;
          const v2 = parseFloat(p.valor_tabela2) || 0;
          return acc + v1 + v2;
        }, 0);
      };

      // Meus pedidos (do usuário logado) - verificar campo 'responsavel' ou 'vendedor'
      const nomeUsuario = usuario?.nome;
      const meusPedidosHoje = pedidosHoje.filter(p => 
        p.responsavel === nomeUsuario || p.vendedor === nomeUsuario
      );
      const meusPedidosMes = pedidosMes?.filter(p => 
        p.responsavel === nomeUsuario || p.vendedor === nomeUsuario
      ) || [];
      const meusPedidosSemana = pedidosSemana?.filter(p => 
        p.responsavel === nomeUsuario || p.vendedor === nomeUsuario
      ) || [];

      // Para vendedores: mostrar apenas seus próprios pedidos nos cards principais
      // Para admin/diretoria/administrativo: mostrar todos os pedidos
      const pedidosExibicaoHoje = podeVerTodosDados ? pedidosHoje : meusPedidosHoje;
      const pedidosExibicaoSemana = podeVerTodosDados ? pedidosSemana : meusPedidosSemana;
      const pedidosExibicaoMes = podeVerTodosDados ? pedidosMes : meusPedidosMes;

      // Pedidos por dia (últimos 7 dias) - usando campo 'data' se existir
      // Para vendedores: mostrar apenas seus pedidos no gráfico
      const pedidosParaGrafico = podeVerTodosDados ? pedidosMes : meusPedidosMes;
      
      // Debug: mostrar todas as datas dos pedidos
      
      const pedidosPorDia = [];
      for (let i = 6; i >= 0; i--) {
        const dia = new Date(hoje);
        dia.setDate(dia.getDate() - i);
        // Usar formato local para comparação correta
        const diaStr = dia.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }); // YYYY-MM-DD
        
        const pedidosDia = pedidosParaGrafico?.filter(p => {
          // Usar campo 'data' se existir, senão converter created_at para fuso de SP
          const dataPedido = getDataPedidoStr(p);
          return dataPedido === diaStr;
        }) || [];
        
        // Debug para cada dia

        pedidosPorDia.push({
          dia: dia.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
          data: dia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
          quantidade: pedidosDia.length,
          valor: calcularValor(pedidosDia)
        });
      }

      // Top vendedores do mês (para admin/diretoria/administrativo)
      let topVendedores = [];
      if (podeVerTodosDados && pedidosMes && pedidosMes.length > 0) {
        const vendedorMap = {};
        pedidosMes.forEach(p => {
          const nomeVendedor = p.responsavel || p.vendedor || 'Não informado';
          if (!nomeVendedor || nomeVendedor === 'Não informado') return;
          
          if (!vendedorMap[nomeVendedor]) {
            vendedorMap[nomeVendedor] = { nome: nomeVendedor, quantidade: 0, valor: 0 };
          }
          vendedorMap[nomeVendedor].quantidade++;
          vendedorMap[nomeVendedor].valor += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0);
        });
        
        topVendedores = Object.values(vendedorMap)
          .filter(v => v.quantidade > 0)
          .sort((a, b) => b.valor - a.valor)
          .slice(0, 5);
      }

      // Últimos 5 pedidos - buscar nome do cliente do JOIN
      // Para vendedores: mostrar apenas seus pedidos
      const pedidosParaUltimos = podeVerTodosDados ? pedidosMes : meusPedidosMes;
      const ultimosPedidos = pedidosParaUltimos?.slice(0, 5).map(p => {
        // Nome do cliente vem do JOIN (clientes.nome) ou campos diretos
        const nomeCliente = p.clientes?.nome || p.cliente || p.nome_cliente || p.razao_social || null;
        
        return {
          id: p.id,
          numero: p.numero_proposta || `#${p.id}`,
          cliente: nomeCliente,
          valor: (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0),
          status: calcularStatusGeral(p),
          data: formatarDataPedido(p),
          responsavel: p.responsavel || p.vendedor || '-',
          tipoDocumento: p.tipo_documento || 'Orçamento',
          faseAtual: p.fase_atual || '-'
        };
      }) || [];

      setDados({
        // Cards principais: filtrados por usuário (vendedor vê só dele, admin vê todos)
        pedidosHoje: pedidosExibicaoHoje.length,
        pedidosSemana: pedidosExibicaoSemana.length,
        pedidosMes: pedidosExibicaoMes?.length || 0,
        valorHoje: calcularValor(pedidosExibicaoHoje),
        valorSemana: calcularValor(pedidosExibicaoSemana),
        valorMes: calcularValor(pedidosExibicaoMes || []),
        pedidosPorDia,
        // Meus números (sempre do usuário logado)
        meusPedidosHoje: meusPedidosHoje.length,
        meusPedidosMes: meusPedidosMes.length,
        meuValorMes: calcularValor(meusPedidosMes),
        topVendedores,
        ultimosPedidos,
        faturado
      });

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Só carregar se usuário estiver definido
    if (usuario && usuario.nome) {
      carregarDados();
    }
    
    // Atualizar a cada 1 minuto (60000ms)
    const interval = setInterval(() => {
      if (usuario && usuario.nome) {
        carregarDados(true);
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [usuario?.nome, ehAdminOuDiretoria, podeVerTodosDados]);

  // ========================================
  // FORMATAR MOEDA
  // ========================================
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const textoPedidos = (qtd) => `${qtd} ${qtd === 1 ? 'pedido' : 'pedidos'}`;

  // Parte "Faturado" do card — mostra "—" se o resumo não carregou (em vez de um R$ 0,00 falso)
  const resumoFaturado = (periodo) => periodo
    ? { valor: formatarMoeda(periodo.valor), subtitulo: textoPedidos(periodo.quantidade) }
    : { valor: '—', subtitulo: 'Não foi possível carregar' };

  // ========================================
  // COMPONENTE: CARD DE ESTATÍSTICA
  // Em cima: faturado (destaque) · Embaixo: lançados no período
  // ========================================
  const CardEstatistica = ({ titulo, faturado, lancado, icone: Icone, cor, onClick }) => (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-2xl
        bg-white/70 backdrop-blur-xl
        border border-white/20
        shadow-lg hover:shadow-xl
        transform hover:scale-[1.02] hover:-translate-y-1
        transition-all duration-300 ease-out
        ${onClick ? 'cursor-pointer' : ''}
        group
      `}
    >
      {/* Barra de cor no topo */}
      <div 
        className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
        style={{ backgroundColor: cor }}
      />
      
      {/* Gradiente decorativo */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
        style={{ background: `radial-gradient(circle, ${cor} 0%, transparent 70%)` }}
      />

      <div className="px-6 pt-6 pb-4 flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 mb-2">{titulo}</p>
          <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1 mb-0.5">
            <CheckCircle2 size={14} />
            Faturado
          </p>
          <p className="text-3xl font-bold text-gray-800">{faturado.valor}</p>
          <p className="text-sm text-gray-500 mt-1">{faturado.subtitulo}</p>
        </div>
        <div
          className="p-3 rounded-xl"
          style={{ backgroundColor: `${cor}15` }}
        >
          <Icone size={28} style={{ color: cor }} />
        </div>
      </div>

      <div className="px-6 pt-3 pb-4 border-t border-dashed border-gray-200 bg-gray-50/60">
        <p className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-0.5">
          <FilePlus size={14} />
          Lançados
        </p>
        <p className="text-lg font-bold text-gray-600">{lancado.valor}</p>
        <p className="text-xs text-gray-400">{lancado.subtitulo}</p>
      </div>

      {onClick && (
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight size={20} className="text-gray-400" />
        </div>
      )}
    </div>
  );

  // ========================================
  // COMPONENTE: BOTÃO DE AÇÃO RÁPIDA
  // ========================================
  const BotaoAcaoRapida = ({ titulo, descricao, icone: Icone, cor, onClick, destaque }) => (
    <button
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-2xl p-6 text-left w-full
        ${destaque 
          ? 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white' 
          : 'bg-white/70 backdrop-blur-xl border border-white/20 text-gray-800'
        }
        shadow-lg hover:shadow-xl
        transform hover:scale-[1.02] hover:-translate-y-1
        transition-all duration-300 ease-out
        group
      `}
    >
      {/* Efeito de brilho */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      <div className="flex items-center gap-4">
        <div 
          className={`
            p-4 rounded-xl
            ${destaque ? 'bg-white/20' : ''}
          `}
          style={!destaque ? { backgroundColor: `${cor}15` } : {}}
        >
          <Icone size={32} style={!destaque ? { color: cor } : {}} className={destaque ? 'text-white' : ''} />
        </div>
        <div className="flex-1">
          <h3 className={`text-lg font-bold ${destaque ? 'text-white' : 'text-gray-800'}`}>
            {titulo}
          </h3>
          <p className={`text-sm ${destaque ? 'text-white/80' : 'text-gray-500'}`}>
            {descricao}
          </p>
        </div>
        <ChevronRight 
          size={24} 
          className={`
            transform group-hover:translate-x-1 transition-transform
            ${destaque ? 'text-white/80' : 'text-gray-400'}
          `} 
        />
      </div>
    </button>
  );

  // ========================================
  // COMPONENTE: MINI GRÁFICO DE BARRAS COM TOOLTIP
  // ========================================
  const MiniGrafico = ({ dados }) => {
    const [tooltipData, setTooltipData] = useState(null);
    const maxValor = Math.max(...dados.map(d => d.quantidade), 1);

    const handleMouseEnter = (item) => {
      setTooltipData(item);
    };

    const handleMouseLeave = () => {
      setTooltipData(null);
    };
    
    return (
      <div className="relative">
        {/* Tooltip fixo no topo */}
        <div className={`
          absolute top-0 left-1/2 transform -translate-x-1/2 
          bg-gray-900 text-white px-4 py-2 rounded-xl shadow-xl text-sm
          transition-all duration-200 z-10
          ${tooltipData ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}
        `}>
          {tooltipData && (
            <div className="flex items-center gap-4 whitespace-nowrap">
              <span className="font-bold text-cyan-400">{tooltipData.data}</span>
              <span className="text-gray-400">|</span>
              <span><span className="text-gray-400">Pedidos:</span> <span className="font-bold">{tooltipData.quantidade}</span></span>
              <span className="text-gray-400">|</span>
              <span><span className="text-gray-400">Valor:</span> <span className="font-bold text-green-400">{formatarMoeda(tooltipData.valor)}</span></span>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-3 h-32 pt-8">
          {dados.map((item, index) => (
            <div 
              key={index} 
              className="flex-1 flex flex-col items-center gap-1"
              onMouseEnter={() => handleMouseEnter(item)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="w-full relative flex flex-col items-center justify-end h-28 cursor-pointer group">
                <div 
                  className={`
                    w-full rounded-t-lg bg-gradient-to-t from-blue-600 to-cyan-400 
                    transition-all duration-300 
                    group-hover:from-blue-500 group-hover:to-cyan-300
                    group-hover:shadow-lg group-hover:shadow-cyan-500/30
                    ${tooltipData?.data === item.data ? 'from-blue-500 to-cyan-300 shadow-lg shadow-cyan-500/30 scale-105' : ''}
                  `}
                  style={{ 
                    height: `${(item.quantidade / maxValor) * 100}%`,
                    minHeight: item.quantidade > 0 ? '12px' : '4px',
                    opacity: item.quantidade > 0 ? 1 : 0.3
                  }}
                />
                {item.quantidade > 0 && (
                  <span className={`
                    absolute -top-6 text-xs font-bold transition-colors
                    ${tooltipData?.data === item.data ? 'text-blue-600' : 'text-gray-600'}
                  `}>
                    {item.quantidade}
                  </span>
                )}
              </div>
              <span className={`
                text-xs font-medium capitalize transition-colors
                ${tooltipData?.data === item.data ? 'text-blue-600' : 'text-gray-500'}
              `}>
                {item.dia}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading || !usuario) {
    return <Loading texto="Carregando seu painel..." tamanho="lg" fullScreen={false} />;
  }

  // ========================================
  // RENDER PRINCIPAL
  // ========================================
  return (
    <div className="max-w-7xl mx-auto pb-10 mt-4">
      {/* ========================================
          HEADER COM SAUDAÇÃO
          ======================================== */}
      <div className="mb-8 pt-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Olá, {usuario?.nome?.split(' ')[0]}! 👋
            </h1>
            <p className="text-gray-500 text-base">
              {new Date().toLocaleDateString('pt-BR', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </p>
          </div>
          <button
            onClick={() => carregarDados(true)}
            disabled={refreshing}
            className={`
              flex items-center gap-2 px-5 py-2.5 rounded-xl
              bg-white/70 backdrop-blur-xl border border-white/20
              text-gray-600 hover:text-blue-600
              shadow hover:shadow-md
              transition-all duration-300
              ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="text-sm font-medium">Atualizar</span>
          </button>
        </div>
      </div>

      {/* ========================================
          CARDS DE ESTATÍSTICAS
          ======================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <CardEstatistica
          titulo="Pedidos Hoje"
          faturado={resumoFaturado(dados.faturado?.hoje)}
          lancado={{ valor: formatarMoeda(dados.valorHoje), subtitulo: textoPedidos(dados.pedidosHoje) }}
          icone={Calendar}
          cor={cores.azulMedio}
          onClick={() => onNavigate('pedidos')}
        />
        <CardEstatistica
          titulo="Esta Semana"
          faturado={resumoFaturado(dados.faturado?.semana)}
          lancado={{ valor: formatarMoeda(dados.valorSemana), subtitulo: textoPedidos(dados.pedidosSemana) }}
          icone={TrendingUp}
          cor={cores.ciano}
          onClick={() => onNavigate('pedidos')}
        />
        <CardEstatistica
          titulo="Este Mês"
          faturado={resumoFaturado(dados.faturado?.mes)}
          lancado={{ valor: formatarMoeda(dados.valorMes), subtitulo: textoPedidos(dados.pedidosMes) }}
          icone={Package}
          cor={cores.verde}
          onClick={() => onNavigate('pedidos')}
        />
        <CardEstatistica
          titulo="Ticket Médio"
          faturado={{
            valor: dados.faturado
              ? formatarMoeda(dados.faturado.mes.quantidade > 0 ? dados.faturado.mes.valor / dados.faturado.mes.quantidade : 0)
              : '—',
            subtitulo: 'Por pedido faturado no mês'
          }}
          lancado={{
            valor: formatarMoeda(dados.pedidosMes > 0 ? dados.valorMes / dados.pedidosMes : 0),
            subtitulo: 'Por pedido lançado no mês'
          }}
          icone={DollarSign}
          cor={cores.roxo}
        />
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-8"></div>

      {/* ========================================
          AÇÕES RÁPIDAS
          ======================================== */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Zap size={24} className="text-yellow-500" />
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {temPermissao('pode_criar_pedido') && (
            <BotaoAcaoRapida
              titulo="Novo Pedido"
              descricao="Criar uma nova proposta comercial"
              icone={FilePlus}
              cor={cores.verde}
              onClick={() => onNavigate('novo-pedido')}
              destaque={true}
            />
          )}
          {temPermissao('pode_ver_pedidos') && (
            <BotaoAcaoRapida
              titulo="Consultar Pedidos"
              descricao="Ver e gerenciar todos os pedidos"
              icone={ClipboardList}
              cor={cores.azulMedio}
              onClick={() => onNavigate('pedidos')}
            />
          )}
          {temPermissao('pode_ver_dashboard') && (
            <BotaoAcaoRapida
              titulo="Dashboard"
              descricao="Análises e relatórios detalhados"
              icone={BarChart3}
              cor={cores.ciano}
            onClick={() => onNavigate('dashboard')}
          />
          )}
        </div>
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-8"></div>

      {/* ========================================
          GRID: GRÁFICO + MEUS NÚMEROS / TOP VENDEDORES
          ======================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Gráfico dos últimos 7 dias */}
        <div className="lg:col-span-2 bg-white/70 backdrop-blur-xl rounded-2xl p-6 pb-4 border border-white/20 shadow-lg hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300 ease-out flex flex-col">
          <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
            <TrendingUp size={20} className="text-blue-600" />
            Pedidos nos Últimos 7 Dias
          </h3>
          <div className="flex-1 flex items-end">
            <div className="w-full">
              <MiniGrafico dados={dados.pedidosPorDia} />
            </div>
          </div>
        </div>

        {/* Card: Meus Números OU Top Vendedores */}
        {podeVerTodosDados ? (
          // TOP REPRESENTANTES (Admin/Diretoria/Administrativo)
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Award size={20} className="text-yellow-500" />
              Top Representantes do Mês
            </h3>
            {dados.topVendedores.length > 0 ? (
              <div className="space-y-3">
                {dados.topVendedores.map((vendedor, index) => (
                  <div 
                    key={vendedor.nome}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0
                      ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-amber-200' : 
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white' :
                        'bg-blue-100 text-blue-600'}
                    `}>
                      {index + 1}º
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate" title={vendedor.nome}>
                        {vendedor.nome}
                      </p>
                      <p className="text-xs text-gray-500">
                        {vendedor.quantidade} {vendedor.quantidade === 1 ? 'pedido' : 'pedidos'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-green-600 text-sm">{formatarMoeda(vendedor.valor)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">Nenhum pedido este mês</p>
              </div>
            )}
          </div>
        ) : (
          // MEUS NÚMEROS (Vendedores)
          <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out cursor-default">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Target size={20} />
              Meus Números
            </h3>
            <div className="space-y-4">
              <div className="bg-white/20 rounded-xl p-4 hover:bg-white/30 transition-colors duration-200">
                <p className="text-white/80 text-sm">Pedidos Hoje</p>
                <p className="text-3xl font-bold">{dados.meusPedidosHoje}</p>
              </div>
              <div className="bg-white/20 rounded-xl p-4 hover:bg-white/30 transition-colors duration-200">
                <p className="text-white/80 text-sm">Pedidos no Mês</p>
                <p className="text-3xl font-bold">{dados.meusPedidosMes}</p>
              </div>
              <div className="bg-white/20 rounded-xl p-4 hover:bg-white/30 transition-colors duration-200">
                <p className="text-white/80 text-sm">Valor Total no Mês</p>
                <p className="text-2xl font-bold">{formatarMoeda(dados.meuValorMes)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-8"></div>

      {/* ========================================
          ÚLTIMOS PEDIDOS
          ======================================== */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300 ease-out">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Clock size={20} className="text-gray-500" />
            Últimos Pedidos
          </h3>
          <button
            onClick={() => onNavigate('pedidos')}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            Ver todos <ChevronRight size={16} />
          </button>
        </div>

        {dados.ultimosPedidos.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                  <th className="pb-3 font-medium">Proposta</th>
                  <th className="pb-3 font-medium">Cliente</th>
                  {podeVerTodosDados && <th className="pb-3 font-medium">Responsável</th>}
                  <th className="pb-3 font-medium text-right">Valor</th>
                  <th className="pb-3 font-medium text-center">Tipo</th>
                  <th className="pb-3 font-medium text-center">Fase</th>
                  <th className="pb-3 font-medium text-right">Data</th>
                  <th className="pb-3 font-medium text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {dados.ultimosPedidos.map((pedido) => {
                  // Formatar tipo de documento
                  const tipoFormatado = pedido.tipoDocumento?.toUpperCase() === 'PEDIDO' ? 'Pedido' : 'Orçamento';
                  const isPedido = tipoFormatado === 'Pedido';
                  
                  // Formatar fase atual - usando mesmas cores do statusConfig.js
                  const formatarFase = (fase) => {
                    const fases = {
                      'AGUARDANDO_PROXIMA_ETAPA': { nome: 'Aguardando Próxima Etapa', cor: 'bg-gradient-to-r from-amber-400 to-amber-500 text-white' },
                      'ORCAMENTO_APROVADO': { nome: 'Orçamento Aprovado', cor: 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white' },
                      'SOLICITAR_EDICAO': { nome: 'Solicitada Edição', cor: 'bg-gradient-to-r from-orange-400 to-orange-500 text-white' },
                      'APROVADO_COM_AJUSTES': { nome: 'Aprovado com Ajustes', cor: 'bg-gradient-to-r from-teal-500 to-teal-600 text-white' },
                      'ORCAMENTO_RECUSADO': { nome: 'Orçamento Recusado', cor: 'bg-gradient-to-r from-red-500 to-red-600 text-white' },
                      'EM_PROCESSAMENTO': { nome: 'Em Processamento', cor: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' },
                      'PEDIDO_FATURADO': { nome: 'Pedido Faturado', cor: 'bg-gradient-to-r from-green-600 to-green-700 text-white' },
                      'PEDIDO_CANCELADO': { nome: 'Pedido Cancelado', cor: 'bg-gradient-to-r from-gray-500 to-gray-600 text-white' },
                      'PEDIDO_CANCELADO_POS_FATURAMENTO': { nome: 'Cancelado Após Faturamento', cor: 'bg-gradient-to-r from-rose-600 to-red-700 text-white' },
                      'PEDIDO_CONFIRMADO': { nome: 'Pedido Confirmado', cor: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' },
                      'ARQUIVADO': { nome: 'Arquivado', cor: 'bg-gradient-to-r from-slate-400 to-slate-500 text-white' },
                      'CANCELADO': { nome: 'Cancelado', cor: 'bg-gradient-to-r from-red-500 to-red-600 text-white' },
                    };
                    return fases[fase] || { nome: fase || '-', cor: 'bg-gray-100 text-gray-700' };
                  };
                  
                  const faseInfo = formatarFase(pedido.faseAtual);
                  
                  return (
                  <tr key={pedido.id} className="border-b border-gray-100 hover:bg-blue-50/50 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer">
                    <td className="py-3 font-semibold text-blue-600">{pedido.numero}</td>
                    <td className="py-3 text-gray-700 max-w-[200px]">
                      <span className={`truncate block ${!pedido.cliente ? 'text-gray-400 italic' : ''}`} title={pedido.cliente || 'Não informado'}>
                        {pedido.cliente || 'Não informado'}
                      </span>
                    </td>
                    {podeVerTodosDados && (
                      <td className="py-3 text-gray-600 text-sm">
                        {pedido.responsavel?.split(' ')[0] || '-'}
                      </td>
                    )}
                    <td className="py-3 font-semibold text-gray-800 text-right">{formatarMoeda(pedido.valor)}</td>
                    <td className="py-3 text-center">
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${isPedido ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}
                      `}>
                        {tipoFormatado}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${faseInfo.cor}`}>
                        {faseInfo.nome}
                      </span>
                    </td>
                    <td className="py-3 text-gray-500 text-sm text-right">{pedido.data}</td>
                    <td className="py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navegar diretamente para o pedido usando a rota
                          navigate(`/consulta-pedidos/${pedido.numero}`);
                        }}
                        className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-all hover:scale-110"
                        title="Visualizar pedido"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Nenhum pedido encontrado</p>
          </div>
        )}
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-8"></div>

    </div>
  );
};

export default Home;